import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './DateRangePicker.css';

interface DateRange {
  startDate: Date;
  endDate: Date;
}

interface DateRangePickerProps {
  selectedRange: DateRange;
  onRangeChange: (range: DateRange) => void;
  showPresets?: boolean;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  selectedRange,
  onRangeChange,
  showPresets = true
}) => {
  const [startDate, setStartDate] = useState<Date>(selectedRange.startDate);
  const [endDate, setEndDate] = useState<Date>(selectedRange.endDate);

  const handleStartDateChange = (date: Date | null) => {
    if (date) {
      setStartDate(date);
      // If end date is before start date, adjust it
      if (endDate < date) {
        const newEndDate = new Date(date);
        newEndDate.setDate(date.getDate() + 6); // Default to 1 week
        setEndDate(newEndDate);
        onRangeChange({ startDate: date, endDate: newEndDate });
      } else {
        onRangeChange({ startDate: date, endDate });
      }
    }
  };

  const handleEndDateChange = (date: Date | null) => {
    if (date) {
      setEndDate(date);
      // If start date is after end date, adjust it
      if (startDate > date) {
        const newStartDate = new Date(date);
        newStartDate.setDate(date.getDate() - 6); // Default to 1 week before
        setStartDate(newStartDate);
        onRangeChange({ startDate: newStartDate, endDate: date });
      } else {
        onRangeChange({ startDate, endDate: date });
      }
    }
  };

  const getWeekStart = (date: Date): Date => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    return new Date(d.setDate(diff));
  };

  const getWeekEnd = (date: Date): Date => {
    const weekStart = getWeekStart(date);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    return weekEnd;
  };

  const handlePresetClick = (preset: string) => {
    const today = new Date();
    let newStartDate: Date;
    let newEndDate: Date;

    switch (preset) {
      case 'thisWeek':
        newStartDate = getWeekStart(today);
        newEndDate = getWeekEnd(today);
        break;
      case 'nextWeek':
        const nextWeek = new Date(today);
        nextWeek.setDate(today.getDate() + 7);
        newStartDate = getWeekStart(nextWeek);
        newEndDate = getWeekEnd(nextWeek);
        break;
      case 'thisMonth':
        newStartDate = new Date(today.getFullYear(), today.getMonth(), 1);
        newEndDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      case 'nextMonth':
        newStartDate = new Date(today.getFullYear(), today.getMonth() + 1, 1);
        newEndDate = new Date(today.getFullYear(), today.getMonth() + 2, 0);
        break;
      case 'lastWeek':
        const lastWeek = new Date(today);
        lastWeek.setDate(today.getDate() - 7);
        newStartDate = getWeekStart(lastWeek);
        newEndDate = getWeekEnd(lastWeek);
        break;
      case 'lastMonth':
        newStartDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        newEndDate = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      default:
        return;
    }

    setStartDate(newStartDate);
    setEndDate(newEndDate);
    onRangeChange({ startDate: newStartDate, endDate: newEndDate });
  };

  const presets = [
    { key: 'thisWeek', label: 'This Week' },
    { key: 'nextWeek', label: 'Next Week' },
    { key: 'lastWeek', label: 'Last Week' },
    { key: 'thisMonth', label: 'This Month' },
    { key: 'nextMonth', label: 'Next Month' },
    { key: 'lastMonth', label: 'Last Month' }
  ];

  return (
    <div className="date-range-picker">
      <div className="date-inputs">
        <div className="date-input-group">
          <label>Start Date</label>
          <DatePicker
            selected={startDate}
            onChange={handleStartDateChange}
            selectsStart
            startDate={startDate}
            endDate={endDate}
            className="date-input"
            dateFormat="MMM d, yyyy"
            placeholderText="Select start date"
          />
        </div>
        
        <div className="date-separator">to</div>
        
        <div className="date-input-group">
          <label>End Date</label>
          <DatePicker
            selected={endDate}
            onChange={handleEndDateChange}
            selectsEnd
            startDate={startDate}
            endDate={endDate}
            minDate={startDate}
            className="date-input"
            dateFormat="MMM d, yyyy"
            placeholderText="Select end date"
          />
        </div>
      </div>

      {showPresets && (
        <div className="date-presets">
          <span className="presets-label">Quick Select:</span>
          <div className="preset-buttons">
            {presets.map(preset => (
              <button
                key={preset.key}
                className="preset-button"
                onClick={() => handlePresetClick(preset.key)}
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="selected-range-info">
        <span className="range-info">
          Selected: {startDate.toLocaleDateString()} - {endDate.toLocaleDateString()}
        </span>
        <span className="range-duration">
          ({Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1} days)
        </span>
      </div>
    </div>
  );
};

export default DateRangePicker;